import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  type OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroCheckCircle,
  heroExclamationTriangle,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import {
  UiButton,
  UiDialog,
  UiDialogDescription,
  UiDialogOverlay,
  UiDialogTitle,
  UiFormField,
  UiHint,
  UiInput,
  UiLabel,
  UiSpinner,
} from '../../../shared/ui';
import { CONNECTOR_REGISTRY, resolveConnector } from '../connectors/connector-registry';
import type { ConnectorStrategy } from '../connectors/connector-strategy';
import type { ConnectResult } from '../domain/connect-result';
import type { ProviderCatalogEntry } from '../domain/provider-catalog-entry';

type Step = 'intro' | 'credentials' | 'connecting' | 'success' | 'error' | 'cancelled';

export interface ConnectDialogDoneEvent {
  readonly result: ConnectResult;
}

@Component({
  selector: 'app-connect-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgIcon,
    UiButton,
    UiDialog,
    UiDialogDescription,
    UiDialogOverlay,
    UiDialogTitle,
    UiFormField,
    UiHint,
    UiInput,
    UiLabel,
    UiSpinner,
  ],
  providers: [
    provideIcons({
      heroCheckCircle,
      heroExclamationTriangle,
      heroXMark,
    }),
  ],
  template: `
    <div appDialogOverlay>
      <section
        appDialog
        class="connect-dialog"
        aria-labelledby="connect-dialog-title"
        [ngpDialogModal]="true"
      >
        <header class="head">
          <div>
            <span appUiLabel class="mb-1 block uppercase">Conectar provedor</span>
            <h2 appDialogTitle id="connect-dialog-title">{{ entry().name }}</h2>
            <p appDialogDescription>{{ entry().description }}</p>
          </div>
          <button
            appButton
            variant="ghost"
            size="sm"
            type="button"
            (click)="cancel()"
            [disabled]="step() === 'connecting'"
            aria-label="Fechar"
          >
            <ng-icon name="heroXMark" size="17" aria-hidden="true" />
          </button>
        </header>

        <div class="steps" aria-hidden="true">
          @for (label of stepDots; track label.key; let i = $index) {
            <span
              class="dot"
              [class.dot--current]="label.key === step()"
              [class.dot--done]="isDotDone(i)"
            ></span>
          }
        </div>

        <div class="body">
          @switch (step()) {
            @case ('intro') {
              <div class="intro">
                <p appUiHint class="intro-hint">{{ introText() }}</p>
                <ul class="bullets">
                  <li>Não armazenamos sua senha — apenas o token de acesso autorizado.</li>
                  <li>Você pode desconectar a qualquer momento.</li>
                  @if (entry().docsUrl) {
                    <li>
                      <a [href]="entry().docsUrl" target="_blank" rel="noreferrer noopener">
                        Documentação oficial ↗
                      </a>
                    </li>
                  }
                </ul>
              </div>
            }
            @case ('credentials') {
              <div class="intro">
                @if (credentialFormSpec()?.description) {
                  <p appUiHint class="intro-hint">{{ credentialFormSpec()!.description }}</p>
                }
                <form
                  [formGroup]="credentialForm"
                  (ngSubmit)="submitCredentials()"
                  class="flex flex-col gap-3 mt-2"
                >
                  @for (field of credentialFormSpec()?.fields ?? []; track field.name) {
                    <app-ui-form-field
                      [label]="field.label"
                      [required]="true"
                      [hint]="field.helpText"
                    >
                      <input
                        appInput
                        [formControlName]="field.name"
                        [placeholder]="field.placeholder ?? ''"
                        [type]="field.secret ? 'password' : 'text'"
                      />
                      @if (field.helpUrl) {
                        <a
                          class="text-xs underline text-muted mt-1"
                          [href]="field.helpUrl"
                          target="_blank"
                          rel="noreferrer noopener"
                        >Onde encontro?</a>
                      }
                    </app-ui-form-field>
                  }
                </form>
              </div>
            }
            @case ('connecting') {
              <div class="centered">
                <app-ui-spinner aria-hidden="true" />
                <p>Validando credenciais…</p>
                <p appUiHint class="centered-hint">Conectando ao provedor.</p>
              </div>
            }
            @case ('success') {
              <div class="centered">
                <div class="icon icon--success" aria-hidden="true">
                  <ng-icon name="heroCheckCircle" size="36" />
                </div>
                <p class="title">Conectado!</p>
                <p appUiHint class="centered-hint">{{ entry().name }} agora aparece em Conectadas.</p>
              </div>
            }
            @case ('error') {
              <div class="centered">
                <div class="icon icon--error" aria-hidden="true">
                  <ng-icon name="heroExclamationTriangle" size="36" />
                </div>
                <p class="title">Falha ao conectar</p>
                <p appUiHint class="centered-hint">{{ errorMessage() }}</p>
              </div>
            }
            @case ('cancelled') {
              <div class="centered">
                <p class="title">Conexão cancelada</p>
                <p appUiHint class="centered-hint">{{ cancelledText() }}</p>
              </div>
            }
          }
        </div>

        <footer class="actions">
          @switch (step()) {
            @case ('intro') {
              <button appButton variant="ghost" type="button" (click)="cancel()">Cancelar</button>
              <button
                appButton
                variant="primary"
                type="button"
                (click)="start()"
                [disabled]="!connectorReady()"
              >
                {{ connectorReady() ? 'Conectar agora' : 'Preparando conexão…' }}
              </button>
            }
            @case ('credentials') {
              <button appButton variant="ghost" type="button" (click)="cancel()">Cancelar</button>
              <button
                appButton
                variant="primary"
                type="button"
                (click)="submitCredentials()"
                [disabled]="credentialForm.invalid"
              >
                {{ credentialFormSpec()?.submitLabel ?? 'Conectar' }}
              </button>
            }
            @case ('connecting') {
              <button appButton variant="ghost" type="button" disabled>Cancelar</button>
              <button appButton variant="primary" type="button" disabled>Conectando…</button>
            }
            @case ('success') {
              <button appButton variant="primary" type="button" (click)="finish()">
                Concluir
              </button>
            }
            @case ('error') {
              <button appButton variant="ghost" type="button" (click)="cancel()">Fechar</button>
              <button
                appButton
                variant="primary"
                type="button"
                (click)="start()"
                [disabled]="!recoverable()"
              >
                Tentar novamente
              </button>
            }
            @case ('cancelled') {
              <button appButton variant="ghost" type="button" (click)="cancel()">Fechar</button>
              <button appButton variant="primary" type="button" (click)="start()">
                Tentar novamente
              </button>
            }
          }
        </footer>
      </section>
    </div>
  `,
  styleUrl: './connect-dialog.css',
})
export class ConnectDialogComponent implements OnInit {
  private readonly registry = inject(CONNECTOR_REGISTRY);
  private readonly destroyRef = inject(DestroyRef);

  readonly entry = input.required<ProviderCatalogEntry>();
  readonly closed = output<void>();
  readonly done = output<ConnectDialogDoneEvent>();

  readonly step = signal<Step>('intro');
  readonly errorMessage = signal<string>('');
  readonly recoverable = signal(true);

  readonly stepDots = [
    { key: 'intro' as const, label: 'Início' },
    { key: 'connecting' as const, label: 'Autorização' },
    { key: 'success' as const, label: 'Concluído' },
  ];

  readonly stepIndex = computed(() => {
    const map: Record<Step, number> = {
      intro: 0,
      credentials: 1,
      connecting: 1,
      success: 2,
      error: 1,
      cancelled: 1,
    };
    return map[this.step()];
  });

  readonly currentStrategy = computed<ConnectorStrategy | null>(() => {
    return resolveConnector(this.registry, this.entry().connectorKey);
  });

  readonly credentialFormSpec = computed(() => this.currentStrategy()?.credentialForm ?? null);

  /** Conector pronto pra conectar. Quando ele expõe `ready` (ex.: Meta, que aquece o SDK),
   *  o botão fica em "Preparando conexão…" até o SDK carregar — garantindo que o clique dispare
   *  o login síncrono no gesto (popup, não aba). Sem `ready` = sempre pronto. */
  readonly connectorReady = computed(() => this.currentStrategy()?.ready?.() ?? true);

  ngOnInit(): void {
    // Aquece recursos externos do conector (ex.: SDK do Facebook) enquanto o usuário lê o intro,
    // pra que "Conectar agora" dispare a janela dentro do gesto do clique.
    this.currentStrategy()?.preload?.();
  }

  /** Texto agnostico no step "intro": delega pro connector quando ele define introHint,
   *  senao usa fallback generico (sem nada hardcoded de provider). */
  readonly introText = computed(
    () => this.currentStrategy()?.introHint
      ?? `Vamos conectar sua conta ${this.entry().name}. Siga as instrucoes na proxima etapa.`,
  );

  readonly cancelledText = computed(
    () => this.currentStrategy()?.cancelledHint
      ?? 'A conexao foi cancelada antes de concluir.',
  );

  private readonly fb = inject(FormBuilder);

  readonly credentialForm = this.fb.group({} as Record<string, unknown>);

  isDotDone(index: number): boolean {
    return index < this.stepIndex();
  }

  start(): void {
    const strategy = this.currentStrategy();
    if (!strategy) {
      this.errorMessage.set('Nenhum conector configurado para este provedor.');
      this.recoverable.set(false);
      this.step.set('error');
      return;
    }

    if (strategy.credentialForm && strategy.submitCredentials) {
      this.initCredentialForm(strategy.credentialForm.fields);
      this.step.set('credentials');
      return;
    }

    this.step.set('connecting');
    strategy
      .start({ entry: this.entry() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => this.applyResult(result),
        error: () => {
          this.errorMessage.set('Erro inesperado ao iniciar a conexão.');
          this.recoverable.set(true);
          this.step.set('error');
        },
      });
  }

  submitCredentials(): void {
    const strategy = this.currentStrategy();
    if (!strategy?.submitCredentials) return;
    if (this.credentialForm.invalid) return;
    const values = this.credentialForm.getRawValue() as Record<string, string>;
    this.step.set('connecting');
    strategy
      .submitCredentials({ entry: this.entry(), values })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => this.applyResult(result),
        error: () => {
          this.errorMessage.set('Erro inesperado ao validar as credenciais.');
          this.recoverable.set(true);
          this.step.set('error');
        },
      });
  }

  private initCredentialForm(fields: readonly { name: string }[]): void {
    const formGroup = this.credentialForm as unknown as {
      controls: Record<string, unknown>;
      removeControl(name: string): void;
      addControl(name: string, control: unknown): void;
    };
    Object.keys(formGroup.controls).forEach((k) => formGroup.removeControl(k));
    for (const field of fields) {
      formGroup.addControl(field.name, this.fb.control('', { validators: [Validators.required] }));
    }
  }

  cancel(): void {
    this.done.emit({ result: { kind: 'cancelled' } });
    this.closed.emit();
  }

  finish(): void {
    this.closed.emit();
  }

  private applyResult(result: ConnectResult): void {
    if (result.kind === 'success') {
      this.step.set('success');
      this.done.emit({ result });
      return;
    }
    if (result.kind === 'cancelled') {
      this.step.set('cancelled');
      return;
    }
    this.errorMessage.set(result.message);
    this.recoverable.set(result.recoverable);
    this.step.set('error');
    // Emitted even on error (unlike before) — the connect call may have actually SUCCEEDED
    // server-side despite the error the user sees here (e.g. a race between two submissions of the
    // same OAuth code, only one of which the browser was listening to). The parent re-syncs its list
    // against real server state either way, so a hidden success is never left invisible.
    this.done.emit({ result });
  }
}
